import { CircleDashed } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import ToolbarPopover from '~/components/toolbar/shared/ToolbarPopover';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import { adjacentSliceGhostsEnabledState } from '~/state/atoms/viewerAtoms';
import AdjacentSliceGhostsOptions from './AdjacentSliceGhostsOptions';

/**
 * Adjacent-slice contour ghosts: a display mode for every viewport that
 * draws the selected structure's contour from the slices next to the
 * displayed one as interrupted lines. The button opens the switch and the
 * choice of slices, and stays lit while the mode is on; without a visible,
 * contoured structure selected, or with the active structure set on no view,
 * there is nothing to ghost, and it says so.
 */
export default function AdjacentSliceGhostsTool() {
    const intl = useIntl();
    const anchorRef = useRef<HTMLElement | null>(null);
    const [open, setOpen] = useState(false);
    const { hasSelectedRoi, isPointRoi, selectedRoi, targetUnresolved } =
        useStructureToolGating();
    const enabled = useRecoilValue(adjacentSliceGhostsEnabledState);
    const canGhost =
        !targetUnresolved &&
        hasSelectedRoi &&
        !isPointRoi &&
        !!selectedRoi?.visible;

    return (
        <>
            <ToolbarButton
                icon={<CircleDashed />}
                tooltip={
                    targetUnresolved
                        ? buildToolTooltip(
                              intl,
                              intl.formatMessage({
                                  id: 'toolbar.adjacentSliceGhosts.tooltip',
                              }),
                              intl.formatMessage({
                                  id: 'tool.disabled.gridTargetUnresolved',
                              })
                          )
                        : intl.formatMessage({
                              id: canGhost
                                  ? 'toolbar.adjacentSliceGhosts.tooltip'
                                  : 'toolbar.adjacentSliceGhosts.noSelection',
                          })
                }
                disabled={!canGhost}
                selected={enabled || open}
                ariaPressed={enabled}
                anchorRef={anchorRef}
                onClick={() => setOpen((previous) => !previous)}
                data-testid="tool-adjacent-slice-ghosts"
            />
            <ToolbarPopover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
            >
                <AdjacentSliceGhostsOptions />
            </ToolbarPopover>
        </>
    );
}
